import React from 'react';
import { useLocation } from 'react-router-dom';

import { Alert as AntdAlert } from 'antd';

import {
  ComputedResponseDtoListActionsEnum,
  ComputedResponseDtoListAvailableFilesEnum,
} from 'lib_api/lib/api/gen';

import ActionAndFileWrapper from 'components/ActionAndFileWrapper/ActionAndFileWrapper';
import { AvailableAction } from 'components/ActionAndFileWrapper/types';
import ModalControlerDonneesVehicule from 'components/FicheDescriptiveDisplay/ControlerDonneesVehicule/ModalControlerDonneesVehicule';
import CoordonneesProprietaireForm from 'components/FicheDescriptiveDisplay/CoordonneesProprietaire/CoordonneesProprietaireForm';
import MainLeveeForm from 'components/FicheDescriptiveDisplay/MainLevee/MainLeveeForm';
import ModalProlongation from 'components/FicheDescriptiveDisplay/Prolongation/ModalProlongation';
import ImporterPieceJointe from 'components/ImporterPieceJointe/ImporterPieceJointe';
import BaseModal from 'components/WrappedComponents/BaseModal';
import { useApi } from 'hooks/ApiStoreContext';
import { useDossierContext } from 'hooks/dossiers/DossierContext';
import { useFOFilterContext } from 'hooks/FOFilterStoreContext';
import { useModal } from 'hooks/modal/useModal';
import { useFetchArPndNotification } from 'hooks/notification/useFetchArPndNotification';
import { useFetchCourrierNotification } from 'hooks/notification/useFetchCourrierNotification';
import { useRefUniteFo } from 'hooks/referentiels/useRefUniteFo';
import { useDisplayActionsRequisesBanner } from 'hooks/useActionsRequises';
import { useUserStore } from 'hooks/UserStoreContext';
import { ActionsRequisesLinkState } from 'utils/actionsRequisesUtils';
import { BaseModalWidthUtils } from 'utils/BaseModalWidthUtils';
import { extractNonnullDossierIdOrThrow } from 'utils/dossierUtils';
import { makeReadable } from 'utils/makeReadable';

import ControleBlocageVenteErreurIdentificationVehicule from './BlocageVente/ControleBlocageVenteErreurIdentificationVehicule';
import { ConfirmationVolVvnr } from './ConfirmationVolVvnr/ConfirmationVolVvnr';
import EditionNotificationForm from './EditionNotification/EditionNotificationForm';
import { ConfirmerNonIdentifiabiliteProprietaire } from './IdentifiabiliteProprietaire/ConfirmerNonIdentifiabiliteProprietaire';
import { ActionSaisieInfoNotification } from './InfosNotification/ActionSaisieInfoNotification';
import { RelanceVenteVol } from './RelanceVenteVol/RelanceVenteVol';
import UpdateDonneesVehiculeFo from './UpdateDonneesVehicule/UpdateDonneesVehiculeFo';
import UpdateEntiteRattachementFO from './UpdateEntitesRattachements/UpdateEntiteRattachementFO';
import { ValidationActionsVvnrHorsSif } from './ValidationActionsVvnrHorsSif/ValidationActionsVvnrHorsSif';

import './ActionsSuiviVehicule.less';

const ActionsSuiviVehicule = (): React.ReactElement => {
  const user = useUserStore();
  const { state } = useLocation() as { state: ActionsRequisesLinkState };

  const updateMainLeveeModal = useModal();
  const updateProlongationMef = useModal(state?.openProlongationMef || false);
  const updateSaisieInfoNotification = useModal(
    state?.openSuiviNotification || false,
  );
  const updateEditionNotification = useModal();
  const updateUpdateInfosProprietaire = useModal(
    state?.openCoordonneesProprietaire || false,
  );
  const updateControlerDonneesVehicule = useModal(
    state?.openControlerDonnees || false,
  );
  const updateRelanceVenteVol = useModal(
    state?.openControleBlocageVenteVol || false,
  );
  const controleBlocageVente = useModal(
    state?.openControleBlocageVenteErreurIdentification,
  );
  const confirmationVolVvnr = useModal(state?.openConfirmationVolVvnr);
  const validationActionsVvnrHorsSif = useModal(
    state?.openValidationActionsVvnrHorsSif,
  );
  const updateEntiteRattachementModal = useModal();

  const updateDonneesVehicule = useModal();
  const importerPieceJointeModal = useModal();
  const determinerIdentifiabiliteProprietaire = useModal(
    state?.openDeterminerIdentifiabiliteProprietaire,
  );

  const foController = useApi().ForceOrdreControllerApi;
  const [{ result: dossier, setDossier }] = useDossierContext();
  const [, , foActionsRequises] = useFOFilterContext();
  const dossierId = extractNonnullDossierIdOrThrow(dossier);

  // display the actions requises banner(s)
  useDisplayActionsRequisesBanner(dossier, foActionsRequises);

  const filesList: ComputedResponseDtoListAvailableFilesEnum[] =
    dossier.computed?.listAvailableFiles ?? [];

  const uniteParent = useRefUniteFo(
    dossier.computed?.uniteFODto?.idCorrelationParent,
  );

  const { fetchCourrierNotification } = useFetchCourrierNotification(
    foController,
    dossierId,
    setDossier,
  );

  const { fetchArPndNotification } = useFetchArPndNotification(
    foController,
    dossierId,
    setDossier,
  );

  function mapComputedListActionsEnumToAvailableAction(
    actionEnum: ComputedResponseDtoListActionsEnum,
  ): AvailableAction[] {
    switch (actionEnum) {
      case ComputedResponseDtoListActionsEnum.SAISIE_NOTIFICATION_MANUELLE:
        return [
          {
            title: "Suivre l'envoi de la notification",
            onClick: updateSaisieInfoNotification.open,
          },
          {
            title: 'Editer la notification',
            onClick: updateEditionNotification.open,
          },
        ];

      case ComputedResponseDtoListActionsEnum.MODIFIER_COORDONNEES_PROPRIETAIRE:
        return [
          {
            title: 'Modifier les coordonnées du propriétaire',
            onClick: updateUpdateInfosProprietaire.open,
          },
        ];

      case ComputedResponseDtoListActionsEnum.MAIN_LEVEE:
        return [
          {
            title: filesList.includes(
              ComputedResponseDtoListAvailableFilesEnum.MAIN_LEVEE_FO,
            )
              ? 'Modifier la mainlevée'
              : 'Editer la mainlevée',
            onClick: updateMainLeveeModal.open,
          },
        ];

      case ComputedResponseDtoListActionsEnum.PROLONGER_MEF:
        return [
          {
            title: 'Prolonger la mise en fourrière administrative',
            onClick: updateProlongationMef.open,
          },
        ];

      case ComputedResponseDtoListActionsEnum.CONTROLER_DONNEES_VEHICULE:
        return [
          {
            title: 'Contrôler les données du véhicule',
            onClick: updateControlerDonneesVehicule.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.RELANCE_VENTE_VOL:
        return [
          {
            title: 'Contrôler la relance de la vente',
            onClick: updateRelanceVenteVol.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.CONTROLE_DONNEES_ERREUR_IDENTIFICATION_VEHICULE:
        return [
          {
            title: 'Contrôler le blocage de la vente',
            onClick: controleBlocageVente.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.UPDATE_ENTITES_RATTACHEMENT_FO_AF:
        return [
          {
            title: 'Modifier les entités de rattachement',
            onClick: updateEntiteRattachementModal.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.CONFIRMER_VOL_VVNR:
        return [
          {
            title: 'Confirmer le vol',
            onClick: confirmationVolVvnr.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.VALIDATION_ACTIONS_VVNR_HORS_SIF:
        return [
          {
            title: 'Valider les actions externes liées au vol',
            onClick: validationActionsVvnrHorsSif.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.UPDATE_DONNEES_VEHICULE:
        return [
          {
            title: "Modifier les données du véhicule et de l'infraction",
            onClick: updateDonneesVehicule.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.IMPORTER_PIECES_JOINTES:
        return [
          {
            title: 'Joindre un document',
            onClick: importerPieceJointeModal.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.CONFIRMER_NON_IDENTIFIABILITE_PROPRIETAIRE:
        return [
          {
            title: "Renseigner que le propriétaire n'est pas identifiable",
            onClick: determinerIdentifiabiliteProprietaire.open,
          },
        ];
      case ComputedResponseDtoListActionsEnum.RECUPERATION_COURRIER_AUTOMATIQUE:
        return [
          {
            title: 'Récupérer le courrier de notification',
            onClick: fetchCourrierNotification,
          },
        ];
      case ComputedResponseDtoListActionsEnum.RECUPERATION_AR_PND_AUTOMATIQUE:
        return [
          {
            title: "Récupérer l'AR ou le PND de notification",
            onClick: fetchArPndNotification,
          },
        ];
      default:
        return [];
    }
  }

  const actions: AvailableAction[] =
    dossier.computed?.listActions?.flatMap(
      mapComputedListActionsEnumToAvailableAction,
    ) ?? [];

  const responsibleIdCorrelationUniteFo =
    dossier.computed?.uniteFODto?.idCorrelationParent ||
    dossier.computed?.uniteFODto?.idCorrelation;
  const responsibleAbreviationUniteFO = makeReadable(
    uniteParent?.abreviation || dossier.computed?.uniteFODto?.abreviation,
  );

  return (
    <>
      {user.idCorrelationUniteFO === responsibleIdCorrelationUniteFo ? (
        <ActionAndFileWrapper
          filesList={filesList}
          piecesJointesList={dossier.computed?.listAvailablePiecesJointes ?? []}
          actions={actions}
          dossierId={dossierId}
        />
      ) : (
        <AntdAlert
          className={'noActionsMessage'}
          message={`Aucune action ne peut être effectuée sur ce dossier car il est rattaché à l’unité ${responsibleAbreviationUniteFO}`}
          type={'info'}
          showIcon={true}
        />
      )}

      {/* Action édition main levée */}
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateMainLeveeModal.visible}
        closeModal={updateMainLeveeModal.close}
      >
        <MainLeveeForm close={updateMainLeveeModal.close} />
      </BaseModal>

      {/* Action modification coordonnées propriétaire */}
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateUpdateInfosProprietaire.visible}
        closeModal={updateUpdateInfosProprietaire.close}
      >
        <CoordonneesProprietaireForm
          controller={foController}
          close={updateUpdateInfosProprietaire.close}
          editable={true}
        />
      </BaseModal>

      {/* Action modification coordonnées notification */}
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateEditionNotification.visible}
        closeModal={updateEditionNotification.close}
      >
        <EditionNotificationForm close={updateEditionNotification.close} />
      </BaseModal>

      {/* prolonger mise en fourriere modal */}
      <ModalProlongation
        visible={updateProlongationMef.visible}
        close={updateProlongationMef.close}
      />

      {/* contrôler les données du véhicule modal */}
      <ModalControlerDonneesVehicule
        visible={updateControlerDonneesVehicule.visible}
        close={updateControlerDonneesVehicule.close}
      />

      {/* contrôle blocage de la vente modal*/}
      <ControleBlocageVenteErreurIdentificationVehicule
        visible={controleBlocageVente.visible}
        close={controleBlocageVente.close}
      ></ControleBlocageVenteErreurIdentificationVehicule>

      {/* renseigner les infos de la notification modal */}
      <ActionSaisieInfoNotification
        visible={updateSaisieInfoNotification.visible}
        close={updateSaisieInfoNotification.close}
      />

      {/* relancer la vente modal */}
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateRelanceVenteVol.visible}
        closeModal={updateRelanceVenteVol.close}
      >
        <RelanceVenteVol close={updateRelanceVenteVol.close} />
      </BaseModal>
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateEntiteRattachementModal.visible}
        closeModal={updateEntiteRattachementModal.close}
      >
        <UpdateEntiteRattachementFO
          closeModal={updateEntiteRattachementModal.close}
        />
      </BaseModal>

      {/* confirmer le vol d'un vvnr */}
      <ConfirmationVolVvnr
        visible={confirmationVolVvnr.visible}
        close={confirmationVolVvnr.close}
      />

      {/* valider les actions externes liées à un vol d'un vvnr */}
      <ValidationActionsVvnrHorsSif
        visible={validationActionsVvnrHorsSif.visible}
        close={validationActionsVvnrHorsSif.close}
      />

      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateDonneesVehicule.visible}
        closeModal={updateDonneesVehicule.close}
      >
        <UpdateDonneesVehiculeFo closeModal={updateDonneesVehicule.close} />
      </BaseModal>

      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={importerPieceJointeModal.visible}
        closeModal={importerPieceJointeModal.close}
      >
        <ImporterPieceJointe closeModal={importerPieceJointeModal.close} />
      </BaseModal>

      {/* Identifier le propriétaire */}
      <BaseModal
        width={BaseModalWidthUtils.smallWidth}
        isVisible={determinerIdentifiabiliteProprietaire.visible}
        closeModal={determinerIdentifiabiliteProprietaire.close}
      >
        <ConfirmerNonIdentifiabiliteProprietaire
          closeModal={determinerIdentifiabiliteProprietaire.close}
        />
      </BaseModal>
    </>
  );
};

export default ActionsSuiviVehicule;
